/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePreloadedQuery } from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import PortfolioUserMemberList from 'src/components/Portfolio/PortfolioShow/PortfolioUserMemberList';
import PortfolioUserMemberListPlaceholder from 'src/components/Portfolio/PortfolioShow/PortfolioUserMemberList/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import buildPortfolioUserMemberListRetryVariables from './helpers/buildPortfolioUserMemberListRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'portfolio.portfolio_show.portfolio_user_member_list.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the portfolio users. Please try again.',
  },
});

export const portfolioUserMemberListRouteQuery = graphql`
  query PortfolioUserMemberListRoute_Query(
    $uuid: ID!
    $count: Int
    $cursor: String
    $start: Timestamp!
    $finish: Timestamp!
  ) {
    portfolio(uuid: $uuid) {
      ...PortfolioUserMemberList_portfolio
        @arguments(count: $count, cursor: $cursor, start: $start, finish: $finish)
    }
  }
`;

const routePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({ uuid: PropTypes.string }),
  }),
});

/**
 * Renders the user member list from a manually retried query reference.
 * @param {object} props - Renderer props.
 * @param {object} props.routeProps - Found-relay route props.
 * @param {object} props.queryRef - Preloaded Relay query reference.
 * @returns {React.ReactNode} Route content.
 */
function PortfolioUserMemberListPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(portfolioUserMemberListRouteQuery, queryRef);
  return <PortfolioUserMemberList {...routeProps} {...queryData} />;
}

PortfolioUserMemberListPreloadedQueryRenderer.propTypes = {
  routeProps: routePropsShape.isRequired,
  queryRef: PropTypes.shape({ dispose: PropTypes.func }).isRequired,
};

/**
 * Renders the portfolio user member list with retry and field-error handling.
 * @param {object} props - Renderer props.
 * @param {object} props.routeProps - Found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} Route content.
 */
function PortfolioUserMemberListRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    portfolioUserMemberListRouteQuery,
    routeProps,
    {
      logLabel: 'PortfolioUserMemberListRoute',
      getRetryVariablesFunc: buildPortfolioUserMemberListRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={PortfolioUserMemberListPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <PortfolioUserMemberListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <PortfolioUserMemberList {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PortfolioUserMemberListRouteRenderer.propTypes = {
  routeProps: routePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the PortfolioUserMemberList route state supplied by found-relay.
 * Network errors are handled before pending props so the local error boundary mounts.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} PortfolioUserMemberList route content or loading state.
 */
export function portfolioUserMemberListRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <PortfolioUserMemberListRouteRenderer
        key={routeProps.match.params.uuid}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <PortfolioUserMemberListRouteRenderer
      key={props.match.params.uuid}
      routeProps={props}
    />
  );
}

export default PortfolioUserMemberListRouteRenderer;
